import "./TrendChartSkeleton.scss";

// Mirrors the Figma loading comp (node 7615:46927): faint grid lines, a mountain/area silhouette,
// rounded y-axis + x-axis bars, plus a legend loading bar below. A shimmer gradient sweeps
// left→right for the wave. Layout matches the real chart (250px graph + gap + legend) so the
// skeleton occupies the same width and height as the loaded TrendChart.
//
// The plot stretches to fill the available width (preserveAspectRatio="none"). The y-axis bar lives
// in its OWN fixed-width SVG so it keeps its width on narrow viewports instead of squishing — yet it
// shares the same shimmer (see Shimmer below), so the wave stays in sync with the plot.
const VIEW_W = 618;
const VIEW_H = 250;
const Y_AXIS_W = 17;

// 5 evenly spaced horizontal grid lines across the plot area.
const GRID_LINES = [9, 62, 115, 168, 221];

// Mountain silhouette path from Figma (intrinsic 592.656 × 156), placed at the plot origin.
const MOUNTAIN_PATH =
  "M50.27 65.9155L0 86.4225V156H592.656V37.3521L551.794 25.6338L460.367 86.4225L347.48 37.3521L272.516 86.4225L170.212 0L74.964 86.4225L50.27 65.9155Z";

// The path's intrinsic width; the plot SVG (preserveAspectRatio="none") stretches it horizontally
// to fill VIEW_W so the silhouette runs edge-to-edge with no empty gap on the right.
const MOUNTAIN_W = 592.656;

// Shared shimmer gradient in userSpaceOnUse units spanning the full VIEW_W, with identical SMIL
// timing on every instance. Because the coordinates live in user space (not per-shape bounding
// boxes) and all clocks start at load, the bright band sits at the SAME user-x in every shape at
// every instant — so the wave stays in sync across the plot AND the separate fixed-width y-axis SVG
// (the y-axis simply reveals the left edge of the same sweep). objectBoundingBox would scale the
// gradient per-shape and desync the wave. Figma: surface-loading-1 (#E4E4E4) → -2 (#FAFAFA).
const Shimmer = ({ id }: { id: string }) => (
  <linearGradient id={id} gradientUnits="userSpaceOnUse" x1="0" y1="0" x2={VIEW_W} y2="0">
    <stop offset="0%" stopColor="#e4e4e4" />
    <stop offset="50%" stopColor="#fafafa" />
    <stop offset="100%" stopColor="#e4e4e4" />
    <animate attributeName="x1" from={-VIEW_W} to={VIEW_W} dur="1.9s" repeatCount="indefinite" />
    <animate attributeName="x2" from="0" to={VIEW_W * 2} dur="1.9s" repeatCount="indefinite" />
  </linearGradient>
);

type Props = {
  ariaLabel: string;
};

const TrendChartSkeleton = ({ ariaLabel }: Props) => (
  <div className="TrendChartSkeleton" data-testid="trend-chart-loading" role="status" aria-label={ariaLabel}>
    <div className="TrendChartSkeleton__chart-row">
      {/* Y-axis bar — fixed width (1:1 viewBox) so it never squishes when the plot shrinks. Its
          shimmer shares the full VIEW_W userSpaceOnUse gradient, so it stays in sync with the plot. */}
      <svg
        className="TrendChartSkeleton__y-axis"
        viewBox={`0 0 ${Y_AXIS_W} ${VIEW_H}`}
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <defs>
          <Shimmer id="trendChartShimmerY" />
        </defs>
        <rect x={0} y={0} width={Y_AXIS_W} height={VIEW_H} rx={8} fill="url(#trendChartShimmerY)" />
      </svg>

      {/* Plot — stretches to fill the remaining width. */}
      <svg
        className="TrendChartSkeleton__plot"
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <defs>
          <Shimmer id="trendChartShimmerPlot" />
        </defs>

        {/* Grid lines */}
        {GRID_LINES.map((y) => (
          <line key={y} x1={0} y1={y} x2={VIEW_W} y2={y} stroke="#e4e4e4" strokeWidth={1} />
        ))}

        {/* Mountain / area silhouette — scaled horizontally to fill the full plot width. */}
        <g transform={`translate(0, 65) scale(${VIEW_W / MOUNTAIN_W}, 1)`}>
          <path d={MOUNTAIN_PATH} fill="url(#trendChartShimmerPlot)" />
        </g>

        {/* Bottom (x-axis labels) bar */}
        <rect x={0} y={230} width={448} height={20} rx={8} fill="url(#trendChartShimmerPlot)" />
      </svg>
    </div>

    {/* Legend loading bar */}
    <svg
      className="TrendChartSkeleton__legend"
      viewBox={`0 0 ${VIEW_W} 16`}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <defs>
        <Shimmer id="trendChartShimmerLegend" />
      </defs>
      <rect x={0} y={0} width={VIEW_W} height={16} rx={8} fill="url(#trendChartShimmerLegend)" />
    </svg>
  </div>
);

export default TrendChartSkeleton;
